import React, { useState, useEffect, useMemo } from 'react';
import {
  Row, Col, Card, CardBody, Button, Table, Input, Label, Alert, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter, Badge, FormGroup
} from 'reactstrap';
import Select from 'react-select';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--background-secondary)',
    borderColor: state.isFocused ? 'var(--accent-color)' : 'var(--border-color)',
    color: 'var(--text-primary)',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(var(--accent-color-rgb), 0.25)' : 'none',
    '&:hover': { borderColor: 'var(--accent-color)' }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--background-primary)',
    border: '1px solid var(--border-color)',
    zIndex: 9999
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'var(--accent-color)' : state.isFocused ? 'var(--hover-color)' : 'transparent',
    color: state.isSelected ? 'var(--background-primary)' : 'var(--text-primary)'
  }),
  singleValue: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  input: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  placeholder: (provided) => ({ ...provided, color: 'var(--text-secondary)' })
};

const SOURCE_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tv', label: 'TV' },
  { value: 'news', label: 'News' },
  { value: 'web', label: 'Web' }
];

const METRIC_TYPE_OPTIONS = [
  { value: 'reach', label: 'Reach (CPM)' },
  { value: 'engagement', label: 'Engagement (per action)' }
];

const SOURCE_COLORS = {
  instagram: 'info',
  facebook: 'primary',
  tv: 'warning',
  news: 'success',
  web: 'secondary'
};

const AdminCpmRates = () => {
  const [rates, setRates] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [filterSource, setFilterSource] = useState(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    source: 'instagram',
    metric_type: 'reach',
    sponsor_object_id: '',
    brand_class: '',
    rate: '',
    modifier: '1.0',
    size_modifier: '',
    effective_from: '2020-01-01',
    effective_to: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await ApiService.getAllTenants();
        setTenants(res.data);
      } catch (err) {
        console.error('Failed to load tenants:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchRates();
    } else {
      setRates([]);
    }
  }, [selectedTenant]);

  const fetchRates = async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const params = { tenant_id: selectedTenant.value };
      if (filterSource) params.source = filterSource.value;
      const res = await ApiService.getCpmRates(params);
      setRates(res.data);
    } catch (err) {
      toast.error('Failed to load CPM rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTenant) fetchRates();
  }, [filterSource]);

  const tenantOptions = useMemo(() =>
    tenants.map(t => ({ value: t.id, label: t.name })),
    [tenants]
  );

  const filteredRates = useMemo(() => {
    let result = rates;
    if (filterSource) {
      result = result.filter(r => r.source === filterSource.value);
    }
    return result;
  }, [rates, filterSource]);

  const handleAdd = () => {
    setEditingRate(null);
    setForm({
      source: 'instagram',
      metric_type: 'reach',
      sponsor_object_id: '',
      brand_class: '',
      rate: '',
      modifier: '1.0',
      size_modifier: '',
      effective_from: '2020-01-01',
      effective_to: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setForm({
      source: rate.source,
      metric_type: rate.metric_type,
      sponsor_object_id: rate.sponsor_object_id || '',
      brand_class: rate.brand_class || '',
      rate: String(rate.rate),
      modifier: String(rate.modifier),
      size_modifier: rate.size_modifier != null ? String(rate.size_modifier) : '',
      effective_from: rate.effective_from ? rate.effective_from.split('T')[0] : '2020-01-01',
      effective_to: rate.effective_to ? rate.effective_to.split('T')[0] : ''
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.rate || !selectedTenant) {
      toast.error('Rate value is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        tenant_id: selectedTenant.value,
        source: form.source,
        metric_type: form.metric_type,
        rate: parseFloat(form.rate),
        modifier: parseFloat(form.modifier) || 1.0,
        sponsor_object_id: form.sponsor_object_id || null,
        brand_class: form.brand_class || null,
        size_modifier: form.size_modifier ? parseFloat(form.size_modifier) : null,
        effective_from: form.effective_from || '2020-01-01',
        effective_to: form.effective_to || null
      };

      if (editingRate) {
        await ApiService.updateCpmRate(editingRate.id, data);
        toast.success('Rate updated');
      } else {
        await ApiService.createCpmRate(data);
        toast.success('Rate created');
      }
      setIsModalOpen(false);
      fetchRates();
    } catch (err) {
      toast.error('Failed to save rate: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rate) => {
    if (!window.confirm(`Delete this ${rate.source} rate?`)) return;
    try {
      await ApiService.deleteCpmRate(rate.id);
      setRates(prev => prev.filter(r => r.id !== rate.id));
      toast.success('Rate deleted');
    } catch (err) {
      toast.error('Failed to delete rate');
    }
  };

  const getScopeLabel = (rate) => {
    const parts = [];
    if (rate.brand_class) parts.push(`Brand: ${rate.brand_class}`);
    if (rate.sponsor_object?.display_name) parts.push(`Object: ${rate.sponsor_object.display_name}`);
    if (rate.sponsor_object_id && !rate.sponsor_object) parts.push(`Object #${rate.sponsor_object_id}`);
    return parts.length > 0 ? parts.join(', ') : 'Default (all)';
  };

  return (
    <Row>
      <Col xs={12}>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="card-title mb-0" style={{ color: 'var(--text-primary)' }}>
                CPM Rate Management
              </h5>
              <Button color="primary" size="sm" onClick={handleAdd} disabled={!selectedTenant}>
                <i className="fas fa-plus me-1"></i> Add Rate
              </Button>
            </div>

            <Row className="mb-4">
              <Col md={5}>
                <Label style={{ color: 'var(--text-primary)' }}>Tenant</Label>
                <Select
                  options={tenantOptions}
                  value={selectedTenant}
                  onChange={setSelectedTenant}
                  placeholder="Select tenant..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Col>
              <Col md={3}>
                <Label style={{ color: 'var(--text-primary)' }}>Source</Label>
                <Select
                  options={SOURCE_OPTIONS}
                  value={filterSource}
                  onChange={setFilterSource}
                  placeholder="All sources..."
                  isClearable
                  styles={customSelectStyles}
                />
              </Col>
            </Row>

            {!selectedTenant ? (
              <p style={{ color: 'var(--text-secondary)' }}>Select a tenant to manage their CPM rates.</p>
            ) : loading ? (
              <div className="text-center py-4"><Spinner color="primary" /></div>
            ) : filteredRates.length === 0 ? (
              <Alert color="info">No rates configured for this tenant. Click "Add Rate" to create one.</Alert>
            ) : (
              <Table responsive hover className="align-middle" size="sm">
                <thead>
                  <tr>
                    <th style={{ color: 'var(--text-primary)' }}>Source</th>
                    <th style={{ color: 'var(--text-primary)' }}>Type</th>
                    <th style={{ color: 'var(--text-primary)' }}>Rate</th>
                    <th style={{ color: 'var(--text-primary)' }}>Modifier</th>
                    <th style={{ color: 'var(--text-primary)' }}>Scope</th>
                    <th style={{ color: 'var(--text-primary)' }}>Effective</th>
                    <th style={{ color: 'var(--text-primary)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRates.map(rate => (
                    <tr key={rate.id}>
                      <td>
                        <Badge color={SOURCE_COLORS[rate.source] || 'secondary'}>
                          {rate.source}
                        </Badge>
                      </td>
                      <td style={{ color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                        {rate.metric_type}
                      </td>
                      <td style={{ color: 'var(--text-primary)' }}>
                        <strong>{rate.rate}</strong>
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {rate.modifier !== '1.0000' && rate.modifier !== 1 ? `×${rate.modifier}` : '-'}
                        {rate.size_modifier && ` (size: ×${rate.size_modifier})`}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                        {getScopeLabel(rate)}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                        {rate.effective_from?.split('T')[0]}
                        {rate.effective_to && ` → ${rate.effective_to.split('T')[0]}`}
                      </td>
                      <td>
                        <Button color="info" size="sm" className="me-1" onClick={() => handleEdit(rate)}>
                          Edit
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleDelete(rate)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)}>
        <ModalHeader toggle={() => setIsModalOpen(false)}>
          {editingRate ? 'Edit CPM Rate' : 'Add CPM Rate'}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Source *</Label>
                <Select
                  options={SOURCE_OPTIONS}
                  value={SOURCE_OPTIONS.find(s => s.value === form.source) || null}
                  onChange={(opt) => setForm(f => ({ ...f, source: opt?.value || '' }))}
                  styles={customSelectStyles}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Metric Type *</Label>
                <Select
                  options={METRIC_TYPE_OPTIONS}
                  value={METRIC_TYPE_OPTIONS.find(m => m.value === form.metric_type) || null}
                  onChange={(opt) => setForm(f => ({ ...f, metric_type: opt?.value || 'reach' }))}
                  styles={customSelectStyles}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Rate *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.rate}
                  onChange={(e) => setForm(f => ({ ...f, rate: e.target.value }))}
                  placeholder="e.g., 79, 595"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  {form.metric_type === 'reach' ? 'CPM (per 1,000 reach)' : 'Per engagement action'}
                </small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Modifier</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={form.modifier}
                  onChange={(e) => setForm(f => ({ ...f, modifier: e.target.value }))}
                  placeholder="1.0"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  Multiplier applied to rate (e.g., 0.19 for TV)
                </small>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Brand Class (optional)</Label>
            <Input
              type="text"
              value={form.brand_class}
              onChange={(e) => setForm(f => ({ ...f, brand_class: e.target.value }))}
              placeholder="e.g., nike, sparebank1 (leave empty for all brands)"
            />
          </FormGroup>

          {form.source === 'news' && (
            <FormGroup>
              <Label style={{ color: 'var(--text-primary)' }}>Size Modifier (news only)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.size_modifier}
                onChange={(e) => setForm(f => ({ ...f, size_modifier: e.target.value }))}
                placeholder="e.g., 0.5 for small, 0.75 for medium, 1.0 for large"
              />
            </FormGroup>
          )}

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Effective From</Label>
                <Input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) => setForm(f => ({ ...f, effective_from: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Effective To (optional)</Label>
                <Input
                  type="date"
                  value={form.effective_to}
                  onChange={(e) => setForm(f => ({ ...f, effective_to: e.target.value }))}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving && <Spinner size="sm" className="me-2" />}
            {editingRate ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>
    </Row>
  );
};

export default AdminCpmRates;
